/**
 * `<VirtualGroupIncludes>` — Admin › Clubs & access, a group's **Includes**
 * (`docs/plans/group-sharing.md`, mock 1):
 *
 *     Includes   ☑ JAS (grp-jas)   ☑ JP (grp-jp)   ☐ PD
 *                ⓘ Adding a member here removes them from JAS and JP
 *
 * Ticking one writes at once through `setOrgIncludes`, and the server's answer
 * is what is shown — it refuses a virtual group among the includes (one level
 * deep), and a box left ticked over a refusal would claim something untrue.
 * A virtual group is not offered as an include for the same reason.
 */

import { useState } from "react";

import { setOrgIncludes, type Org } from "../api/admin";

export interface VirtualGroupIncludesProps {
  org: Org;
  /** Every group, this one among them; it is not offered as its own include. */
  orgs: readonly Org[];
  /** The write landed; the caller re-reads the listing. */
  onChanged: () => void;
  onError: (message: string | null) => void;
  /** Injected for tests; the real route by default. */
  save?: (key: string, includes: string[] | null) => Promise<unknown>;
}

/** `virtual · 3 members` — the badge on a virtual group's row. */
export function virtualBadge(org: Org): string | null {
  if (!org.includes || org.includes.length === 0) return null;
  return typeof org.memberCount === "number" ? `virtual · ${org.memberCount} members` : "virtual";
}

export function VirtualGroupIncludes({
  org,
  orgs,
  onChanged,
  onError,
  save = setOrgIncludes,
}: VirtualGroupIncludesProps) {
  const [busy, setBusy] = useState(false);
  const includes = org.includes ?? [];
  const candidates = orgs.filter(
    (o) => o.key !== org.key && !(o.includes && o.includes.length > 0),
  );
  const nameOf = (key: string) => orgs.find((o) => o.key === key)?.name ?? key;

  const toggle = (key: string, on: boolean) => {
    const next = on ? [...includes, key] : includes.filter((k) => k !== key);
    setBusy(true);
    onError(null);
    void save(org.key, next.length > 0 ? next : null)
      .then(onChanged)
      .catch((error: unknown) => onError(error instanceof Error ? error.message : String(error)))
      .finally(() => setBusy(false));
  };

  return (
    <div>
      <div style={ROW}>
        <span style={LABEL}>Includes</span>
        {candidates.map((each) => (
          <label key={each.key} style={CHECK}>
            <input
              type="checkbox"
              disabled={busy}
              checked={includes.includes(each.key)}
              onChange={(e) => toggle(each.key, e.target.checked)}
            />
            {each.name} <span style={NOTE}>({each.key})</span>
          </label>
        ))}
      </div>
      {includes.length > 0 && (
        <div style={NOTE}>
          ⓘ Adding a member here removes them from {joinNames(includes.map(nameOf))}
        </div>
      )}
    </div>
  );
}

/** `JAS and JP`, `JAS, JP and PD`. */
function joinNames(names: readonly string[]): string {
  if (names.length <= 1) return names.join("");
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

const ROW: React.CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  gap: 10,
  padding: "4px 0",
};

const LABEL: React.CSSProperties = { color: "var(--qf-text-secondary)", minWidth: 64 };

const CHECK: React.CSSProperties = { display: "inline-flex", alignItems: "center", gap: 4 };

const NOTE: React.CSSProperties = { color: "var(--qf-text-muted)", fontSize: "0.85em" };
